import * as React from 'react'
import styled, { css } from 'styled-components'

import { t } from '@owl-nest/localize'
import * as model from '@owl-nest/models'
import { CATEGORY, EVENT_TYPE, useTracking } from '@owl-nest/shadow'
import * as utils from '@owl-nest/utils'

import * as plume from '@ulule/owl-kit-components/next'

import * as projectModel from '../../../../models/project'
import { TRACKING_ACTION, TRACKING_SOURCE } from '../../utils/trackerEnums'

type ProjectFinancedOffboardingProps = {
  project: projectModel.Project
  fullSize: boolean
}

export function ProjectFinancedOffboarding({
  project,
  fullSize,
}: ProjectFinancedOffboardingProps): React.ReactElement<ProjectFinancedOffboardingProps> {
  const offboardingType: string | undefined = project.onboarding_steps?.offboarding?.type

  const tracking = useTracking()

  const boutiqueLink = project.boutique_link?.url
  const postCampaignUrl = project.post_campaign_link?.url

  let button = t('Order')
  if (boutiqueLink) {
    button = t('Buy %(brand)s on Ulule Boutique', { brand: model.user.username(project.owner) })
  } else if (offboardingType === 'support') {
    button = t('Back the project')
  } else if (offboardingType === 'follow') {
    button = t('Follow the project')
  }

  return (
    <ProjectFinancedContainer $fullSize={fullSize}>
      <InformationContainer $fullSize={fullSize}>
        <plume.styles.heading.S asDesktop="L">
          <plume.illustrations.twoToned.CrackedEgg size={60} noBackground inverted />
          {t('%(name)s got funded!', { name: model.user.username(project.owner) })}
        </plume.styles.heading.S>

        <plume.styles.copy.M.light>
          {boutiqueLink ? t('Find this project on the Boutique 👇') : t('Good news, we have continued selling here 👇')}
        </plume.styles.copy.M.light>

        <plume.styles.copy.M.light>
          <plume.styles.button.Button
            size="medium"
            kind="primary"
            as="a"
            href={
              boutiqueLink
                ? utils.searchParams.addSearchParamsToUrl(boutiqueLink, {
                    utm_source: 'page-collecte',
                    utm_medium: 'CTA',
                    utm_campaign: 'page-collecte-vendeur',
                  })
                : postCampaignUrl
            }
            onClick={track}
          >
            {button}
          </plume.styles.button.Button>
        </plume.styles.copy.M.light>

        {postCampaignUrl && boutiqueLink && (
          <ShopCTA>
            <plume.Link tinted darkened uppercase href={postCampaignUrl} target="_blank" rel="noopener noreferrer">
              {t('Buy on the official website')}
              <plume.glyphs.stroke.ArrowRight size={18} />
            </plume.Link>
          </ShopCTA>
        )}
      </InformationContainer>
    </ProjectFinancedContainer>
  )

  function track() {
    if (offboardingType !== undefined) {
      tracking.track({
        event: EVENT_TYPE.GENERIC,
        eventCategory: CATEGORY.PROJECT_PAGE,
        eventAction: TRACKING_ACTION.CLICK_OFFBOARDING,
        eventLabel: TRACKING_SOURCE.BACKER_BLOCK,
        eventValue: offboardingType,
      })
    }
  }
}

const ProjectFinancedContainer = styled.div<{ $fullSize: boolean }>`
  display: block;
  text-align: center;
  padding: 20px 0px;
  width: 100%;

  // NOT SCREEN !
  @media not screen and ${plume.BREAKPOINTS.LAPTOP} {
    ${plume.illustrations.Illustration} {
      margin-bottom: 5px;
    }
  }

  @media screen and ${plume.BREAKPOINTS.LAPTOP} {
    display: flex;
    padding: 0;
    max-width: 790px;

    ${({ $fullSize }) => {
      if ($fullSize) {
        return css`
          justify-content: center;
        `
      } else {
        return css`
          text-align: left;
        `
      }
    }}
  }
`

const InformationContainer = styled.div<{ $fullSize: boolean }>`
  box-sizing: border-box;
  padding: 0 20px;
  width: 100%;

  ${({ $fullSize }) => {
    if ($fullSize) {
      return css`
        align-items: center;
        display: flex;
        flex-direction: column;
      `
    }
  }}

  ${plume.styles.heading.S} {
    align-items: center;
    display: flex;
    margin-bottom: 6px;
  }

  ${plume.styles.button.Button} {
    width: 100%;
  }

  ${plume.Link} {
    margin-top: 20px;
  }

  ${plume.styles.copy.M.light} {
    margin-bottom: 16px;
  }

  // NOT SCREEN !
  @media not screen and ${plume.BREAKPOINTS.LAPTOP} {
    ${plume.styles.heading.S} {
      align-items: center;
      display: flex;
      flex-direction: column;
      justify-content: center;
      margin-bottom: 0px;
    }
  }

  @media screen and ${plume.BREAKPOINTS.LAPTOP} {
    ${plume.styles.heading.S} {
      ${plume.illustrations.Illustration} {
        margin-right: 8px;
      }
    }

    ${plume.styles.button.Button} {
      width: auto;
    }
  }
`

const ShopCTA = styled(plume.styles.copy.M.light)`
  text-align: center;

  ${plume.glyphs.stroke.ArrowRight} {
    margin-left: 11px;
    margin-top: -6px;
    transition: margin-left 0.3s cubic-bezier(0.83, 0, 0.17, 1);
  }

  ${plume.Link} {
    &:hover {
      ${plume.glyphs.stroke.ArrowRight} {
        margin-left: 18px;
      }
    }
  }

  @media screen and ${plume.BREAKPOINTS.LAPTOP} {
    text-align: left;
  }
`
